import React, { FC, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useLocation } from 'react-router-dom';
import Modal from 'components/shared/Modal';
import AutomaticForm from 'components/modals/newSocialMediaProfile/automaticForm/Form';
import ConnectSuccess from 'components/modals/newSocialMediaProfile/success/ConnectSuccess';
import { ModalRouteProps } from 'context/ModalRouter';
import { useTranslation } from 'react-i18next';
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
} from 'use-query-params';
import routes, { modalRoutes, validateModalRoute } from 'constants/routes';
import { ActivityIndicator } from '@kontentino/ui';
import useAddNewProfileAutomatically from 'app/modules/posts/hooks/useAddNewProfileAutomatically';
import { useSocialLoginContext } from 'app/modules/socialLogin/context';
import { usePostsContainer } from 'components/posts/context';
import HeapUtils from 'app/utils/heap';
import { connectTracking } from 'app/modules/socialLogin/utils/connectTracking';
import { ConnectSuccessMethod, NewAddedPage, RefusedProfile } from 'types/Page';
import {
  hasOboPicker,
  isOboEmptyResult,
} from 'app/modules/socialLogin/utils/oboPicker';

type Props = ModalRouteProps & {
  onProfileAdded?: (profileId: number) => void;
  // The host has the network grid (`#new-profile`) to add more profiles from.
  // Settings does not.
  hasNetworkGrid?: boolean;
};

// What the success view needs, taken when the profiles are added: right after,
// the calendar's filter change rewrites the URL to its own parameters, so
// `type` and `nextHash` are gone by the time the person closes the view.
type ConnectResult = {
  count: number;
  method: ConnectSuccessMethod;
  pageType: number;
  refused: RefusedProfile[];
  // The flow began on the disconnected-profiles list, so it ends there.
  returnsToDisconnectedProfiles: boolean;
};

export const AssociateProfileModal: FC<Props> = ({
  modalManager,
  modalHistory,
  onProfileAdded,
  hasNetworkGrid = true,
}) => {
  const { t } = useTranslation();
  const socialLogin = useSocialLoginContext();
  const location = useLocation();
  const [query] = useQueryParams({
    type: NumberParam,
    id: NumberParam,
    reconnect: BooleanParam,
    nextHash: StringParam,
  });
  // Every Reconnect/Refresh entry sends `reconnect=1`. `id` alone does not mean
  // reconnect: other flows name a profile too.
  const isReconnect = !!query.reconnect;
  const automaticPageAdded = useAddNewProfileAutomatically({
    onProfileAdded,
  });
  const hasSavedProfile = useRef(false);
  // Set once profiles are added: the success view replaces the form.
  const [result, setResult] = useState<ConnectResult | null>(null);
  // Both pickers scroll inside themselves: the modal becomes a flex column,
  // so its height cap reaches the picker, whose list scrolls between the
  // search and Add (D10). Inline, so react-responsive-modal still centres it.
  // The Safe Connect picker is two columns wide.
  const isOboPicker = !result && hasOboPicker(socialLogin);
  const isOboEmpty = !result && isOboEmptyResult(socialLogin);
  // Every other login: the one-list picker, at the modal's own width.
  const isClassicPicker =
    !result &&
    !isOboPicker &&
    !Array.isArray(socialLogin.businesses) &&
    !!socialLogin.authorization;
  const isPicker = isOboPicker || isClassicPicker;

  const handlePageAdded = (addedPage: NewAddedPage) => {
    hasSavedProfile.current = true;
    setResult({
      count: addedPage.ids.length,
      method: addedPage.method ?? 'other',
      // The network the person chose; `addedPage.type` is always Facebook.
      pageType: query.type ?? addedPage.type,
      refused: addedPage.refused ?? [],
      returnsToDisconnectedProfiles:
        !!query.nextHash && validateModalRoute(query.nextHash),
    });
    automaticPageAdded.onPageAdded(addedPage);
  };

  const closeSuccess = () => {
    if (result?.returnsToDisconnectedProfiles) {
      modalHistory.replace({ hash: modalRoutes.DISCONNECTED_PROFILES });
      return;
    }

    modalHistory.closeLast();
  };

  // Closing returns to the calendar only when no other modal is open under
  // this one (with the post detail underneath, say, it returns there) and the
  // flow did not begin on the disconnected profiles list.
  // `activeModals` also keeps modals already closed until they are destroyed.
  const hasModalUnderneath = modalHistory.activeModals.some(
    (modal) => modal.open && modal.key !== modalManager.key,
  );
  // Any posts view is the calendar.
  const closesToCalendar =
    location.pathname.startsWith(routes.POSTS) &&
    !hasModalUnderneath &&
    !result?.returnsToDisconnectedProfiles;
  // The network grid replaces the whole hash, which would close the modal
  // underneath; whoever came from there came to fix one profile.
  const canAddMore = hasNetworkGrid && !hasModalUnderneath;

  const addMoreProfiles = () =>
    modalHistory.replace({ hash: modalRoutes.NEW_PROFILE, search: '' });

  const headerTitle = () => {
    if (result || isOboPicker) {
      // In the body: the picker's own title, or the success view's.
      return undefined;
    }

    return isOboEmpty
      ? t('newSocialMediaProfile.oboEmpty.modalTitle')
      : t('associateProfileTitle');
  };

  useEffect(() => {
    return () => {
      const attempt = connectTracking.get();

      if (attempt?.source === 'add_profile') {
        if (!hasSavedProfile.current) {
          HeapUtils.track(HeapUtils.events.CONNECT_FLOW_CLOSED, {
            network: attempt.network,
            last_step: 'select-profiles',
          });
        }
        connectTracking.clear();
      }
      socialLogin.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      modalManager={modalManager}
      size={isOboPicker ? 'lg' : 'sm'}
      classNames={
        isPicker
          ? {
              modal: clsx(
                // The shared cap, min(800px, 90vh), in `dvh`: on a phone `vh`
                // counts the space under the browser's bars, so Add could
                // sit beneath them.
                '!tw-inline-flex tw-max-h-[min(800px,90dvh)] tw-flex-col',
                // Stacked (below `sm`) the Safe Connect picker splits the
                // height between its two lists, which needs a definite
                // height, not only a cap.
                { 'max-sm:tw-h-[min(800px,90dvh)]': isOboPicker },
              ),
            }
          : undefined
      }
      dataName={{
        wrapper: 'associate-social-media-profile-modal',
      }}
    >
      {/* The picker and the success view carry their own titles; the header
          keeps the close button. */}
      <Modal.Header
        title={headerTitle()}
        className={clsx({
          'tw-shrink-0': isPicker,
          'tw-pb-0 max-sm:tw-pt-4': isOboPicker,
          'tw-pb-0': !!result,
        })}
      />
      {result ? (
        <ConnectSuccess
          count={result.count}
          method={result.method}
          pageType={result.pageType}
          refused={result.refused}
          onAddMore={canAddMore ? addMoreProfiles : undefined}
          onClose={closeSuccess}
          closeLabel={closesToCalendar ? 'backToCalendar' : 'done'}
        />
      ) : (
        <Modal.Content
          className={clsx('tw-p-0', {
            'tw-flex tw-min-h-0 tw-flex-1 tw-flex-col': isPicker,
          })}
        >
          {query.type && (
            <AutomaticForm
              {...socialLogin}
              pageType={query.type}
              pageId={query.id ?? undefined}
              isReconnect={isReconnect}
              onPageAdded={handlePageAdded}
              onGoBack={modalHistory.closeLast}
            />
          )}
          {!query.type && (
            <div className="tw-flex tw-items-center tw-justify-center tw-p-10">
              <ActivityIndicator className="tw-h-7 tw-w-7" />
            </div>
          )}
        </Modal.Content>
      )}
    </Modal>
  );
};

const PostsAssociateProfileModal: FC<ModalRouteProps> = (props) => {
  const postsContainer = usePostsContainer();

  return (
    <AssociateProfileModal
      {...props}
      onProfileAdded={(id) =>
        postsContainer.changeFilter({
          key: 'selectedProfiles',
          value: [id],
        })
      }
    />
  );
};

export default PostsAssociateProfileModal;
